import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Lock as LockIcon,
  Check as CheckIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useSelector } from "react-redux";
import { API_URL, createAxiosInstance } from "../../../api";
// @ts-ignore - SVG import
import successIcon from "../../../assets/success-icon.svg";
// @ts-ignore - PNG import
import stripeLogo from "../../../assets/stripelogo.png";

export default function Subscription({ onDirtyChange, onSave }: any) {
  const stripe = useStripe();
  const elements = useElements();
  const userId = useSelector((state: any) => state.profile.user.user);
  
  const [paid, setPaid] = useState(false);
  const [paidStatus, setPaidStatus] = useState("false");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [subscriptionType, setSubscriptionType] = useState("monthly");
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!userId) {
        console.log("User ID is undefined or invalid. Skipping payment check.");
        return;
      }

      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${API_URL}/api/billings/hasPaid/${userId}`
        );

        const paidStatus = response.data?.paid;
        const isPaid = paidStatus === "Paid" || paidStatus === true;

        if (response.data && isPaid) {
          setPaid(true);
          setPaidStatus(response.data.paid);
        } else {
          setPaid(false);
          setPaidStatus("false");
        }
      } catch (error: any) {
        console.error(
          "Error checking payment status:",
          error.response?.data?.message || error.message
        );
        setPaidStatus("false");
      }
    };

    checkPaymentStatus();
    onDirtyChange?.(false);
  }, [userId]);

  const cardElementStyles = {
    base: {
      backgroundColor: "light-grey",
      fontSize: "18px",
      color: "#32325d",
      "::placeholder": {
        color: "#aab7c4",
      },
      border: "1px solid #ccc",
      borderRadius: "4px",
      padding: "10px",
      marginBottom: "16px",
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
    focus: {
      borderColor: "#712d93",
    },
  };

  const handlePayment = async () => {
    if (!stripe || !elements) {
      Swal.fire({
        icon: "error",
        title: "Payment System Loading",
        text: "Please wait while the payment system initializes",
      });
      return;
    }
    setPaymentLoading(true);

    const cardNumberElement = elements.getElement(CardNumberElement);

    if (!cardNumberElement) {
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: "Card element is not available.",
      });
      setPaymentLoading(false);
      return;
    }

    try {
      const { error, token } = await stripe.createToken(cardNumberElement);

      if (error) {
        Swal.fire({
          icon: "error",
          title: "Payment Failed",
          text: error.message,
        });
        setPaymentLoading(false);
        return;
      }

      const axiosInstance = createAxiosInstance();

      if (!firstName.trim() || !lastName.trim()) {
        Swal.fire({
          icon: "error",
          title: "Missing Information",
          text: "Please enter both first and last name",
        });
        setPaymentLoading(false);
        return;
      }

      const response = await axiosInstance.post(`${API_URL}/api/billings/stripe`, {
        token: token.id,
        subscriptionType,
        name: `${firstName} ${lastName}`,
        userId,
      });

      if (response.data && response.data.success) {
        Swal.fire({
          imageUrl: successIcon,
          imageHeight: "100px",
          title: "Payment Successful",
          text: "Your subscription has been activated!",
          confirmButtonColor: "#712d94",
          customClass: {
            icon: "no-before-icon",
          },
        }).then(() => {
          setPaid(true);
          setPaidStatus("Paid");
          onDirtyChange?.(false);
          onSave?.();
          // Dispatch event to notify other components
          window.dispatchEvent(new Event('paymentCompleted'));
        });
      } else {
        throw new Error(response.data.message || "Payment failed");
      }
    } catch (error: any) {
      console.error("Payment processing error:", error);
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text:
          error.response?.data?.message ||
          "There was an issue processing your payment.",
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  (Subscription as any).valid = paidStatus !== "false";
  (Subscription as any).save = async () => {
    onDirtyChange?.(false);
  };

  const monthlyFeatures = ["Flexible", "Cancel anytime"];
  const yearlyFeatures = [
    "Save 17%",
    "Cancel anytime",
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
        Choose Your Subscription Plan
      </Typography>
      <Typography variant="body2" sx={{ mb: 4, color: "#6b7280" }}>
        Plan & payment
      </Typography>

      <Typography
        variant="h5"
        align="center"
        sx={{ mb: 4, fontWeight: 600, color: "#712d94" }}
      >
        Support our platform and grow your tutoring business.
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Monthly Plan */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              backgroundColor: subscriptionType === "monthly" ? "#712d94" : "#ffffff",
              border: subscriptionType === "monthly" 
                ? "2px solid #712d94" 
                : "1px solid #e9e9ec",
              borderRadius: 2,
              height: "100%",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#712d94",
                boxShadow: subscriptionType === "monthly" 
                  ? "0 4px 16px rgba(113, 45, 148, 0.25)" 
                  : "0 4px 12px rgba(113, 45, 148, 0.15)",
                transform: "translateY(-2px)",
              },
            }}
            onClick={() => {
              setSubscriptionType("monthly");
              onDirtyChange?.(true);
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                sx={{ 
                  mb: 2, 
                  fontWeight: 700, 
                  color: subscriptionType === "monthly" ? "#fff" : "#1e293b",
                }}
              >
                Monthly - £2.00
              </Typography>
              <Stack spacing={1.5}>
                {monthlyFeatures.map((feature, index) => (
                  <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckIcon 
                      sx={{ 
                        color: subscriptionType === "monthly" ? "#fff" : "#712d94", 
                        fontSize: "20px" 
                      }} 
                    />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: subscriptionType === "monthly" ? "#fff" : "#334155",
                        fontWeight: subscriptionType === "monthly" ? 500 : 400,
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Yearly Plan */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              backgroundColor: subscriptionType === "yearly" ? "#712d94" : "#ffffff",
              border: subscriptionType === "yearly" 
                ? "2px solid #712d94" 
                : "1px solid #e9e9ec",
              borderRadius: 2,
              height: "100%",
              cursor: "pointer",
              transition: "all 0.2s ease",
              position: "relative",
              "&:hover": {
                borderColor: "#712d94",
                boxShadow: subscriptionType === "yearly" 
                  ? "0 4px 16px rgba(113, 45, 148, 0.25)" 
                  : "0 4px 12px rgba(113, 45, 148, 0.15)",
                transform: "translateY(-2px)",
              },
            }}
            onClick={() => {
              setSubscriptionType("yearly");
              onDirtyChange?.(true);
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ position: "relative", mb: 1 }}>
                <Chip
                  icon={<StarIcon />}
                  label="Best Value"
                  sx={{
                    position: "absolute",
                    top: -12,
                    right: -12,
                    backgroundColor: "#FFC107",
                    color: "#000",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    height: "28px",
                    boxShadow: "0 2px 8px rgba(255, 193, 7, 0.3)",
                    "& .MuiChip-icon": {
                      color: "#000",
                    },
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  color: subscriptionType === "yearly" ? "#fff" : "#1e293b",
                }}
              >
                Yearly - £19.99
              </Typography>
              {subscriptionType !== "yearly" && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  (Save 17%)
                </Typography>
              )}
              <Stack spacing={1.5}>
                {yearlyFeatures.map((feature, index) => (
                  <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckIcon
                      sx={{
                        color: subscriptionType === "yearly" ? "#fff" : "#712d94",
                        fontSize: "20px",
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: subscriptionType === "yearly" ? "#fff" : "#334155",
                        fontWeight: subscriptionType === "yearly" ? 500 : 400,
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography
        variant="body2"
        align="center"
        sx={{ mb: 4, color: "#6b7280", maxWidth: "800px", mx: "auto" }}
      >
        We charge a small subscription fee to cover website hosting, secure payments, and
        continuous improvements - so we can connect you with more students and keep
        everything running smoothly.
      </Typography>

      <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
          mb: 3,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mb: 3,
            }}
          >
            <LockIcon sx={{ color: "#712d94", fontSize: "20px" }} />
            <Typography variant="h5" align="center" sx={{ fontWeight: 600 }}>
              Payment Details (Secure via Stripe)
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  onDirtyChange?.(true);
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  onDirtyChange?.(true);
                }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <CardNumberElement options={{ style: cardElementStyles }} />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <CardExpiryElement options={{ style: cardElementStyles }} />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <CardCvcElement options={{ style: cardElementStyles }} />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={handlePayment}
              loading={paymentLoading}
              disabled={!stripe || paymentLoading || !firstName || !lastName}
              sx={{
                minWidth: "200px",
                height: "48px",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Pay Now
            </LoadingButton>
          </Box>

          {paidStatus !== "false" && (
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                backgroundColor: "rgba(36, 161, 72, 0.1)",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                ✓ Payment Completed Successfully
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <img
              src={stripeLogo}
              alt="Powered by Stripe"
              style={{ maxWidth: "150px", height: "auto" }}
            />
          </Box>
        </CardContent>
      </Card>

      <Typography
        variant="body2"
        align="center"
        sx={{ mt: 3, color: "#6b7280", fontStyle: "italic" }}
      >
        Join 400+ tutors already building their profiles with us.
      </Typography>
    </Box>
  );
}
